import React from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

const LeadSelect = ({
  value,
  onChange,
  error,
  helperText,
  required = false,
  label = 'Select Lead',
  placeholder,
  size = 'small',
  fullWidth = true,
  disabled = false,
  options = [],
  sx = {},
}) => {
  const showFloatingLabel = Boolean(label && String(label).trim());
  // Find the selected lead object based on value
  const getSelectedLead = () => {
    if (!value) return null;
    
    // Handle both direct lead objects and lead IDs
    if (typeof value === 'object' && value !== null) {
      return value;
    }
    
    // If value is an ID, find the lead object
    return options.find(lead => 
      (lead.id && lead.id.toString() === value.toString()) ||
      (lead.lead_id && lead.lead_id.toString() === value.toString()) ||
      (lead.value && lead.value.toString() === value.toString())
    ) || null;
  };

  const selectedLead = getSelectedLead();

  return (
    <Box sx={{ ...sx }}>
      <Autocomplete
        options={options}
        getOptionLabel={option => {
          if (option.name && option.lead_id) {
            return `${option.name} (${option.lead_id})`;
          } else if (option.label && option.lead_id) {
            return `${option.label} (${option.lead_id})`;
          } else if (option.name) {
            return option.name;
          } else if (option.label) {
            return option.label;
          }
          return '';
        }}
        value={selectedLead}
        onChange={(_, newValue) => {
          if (onChange) {
            if (newValue) {
              // Pass both the ID and the full lead object (like AgentSelect)
              const leadId = newValue.id || newValue.lead_id || newValue.value;
              onChange(leadId, newValue);
            } else {
              // Pass null for both when clearing
              onChange(null, null);
            }
          }
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return (
            (option.id && value.id && option.id.toString() === value.id.toString()) ||
            (option.lead_id && value.lead_id && option.lead_id.toString() === value.lead_id.toString()) ||
            (option.value && value.value && option.value.toString() === value.value.toString()) ||
            (option.id && value.lead_id && option.id.toString() === value.lead_id.toString()) ||
            (option.lead_id && value.id && option.lead_id.toString() === value.id.toString())
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={showFloatingLabel ? label : undefined}
            hiddenLabel={!showFloatingLabel}
            placeholder={showFloatingLabel ? undefined : (placeholder || 'Search or select a lead…')}
            variant="outlined"
            size={size}
            error={!!error}
            helperText={error || helperText || (required ? 'Required' : 'Optional')}
            fullWidth={fullWidth}
            InputLabelProps={showFloatingLabel ? { shrink: true } : undefined}
          />
        )}
        disabled={disabled}
        clearOnEscape
      />
    </Box>
  );
};

LeadSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lead_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      label: PropTypes.string
    })
  ),
  sx: PropTypes.object
};

export default LeadSelect;